import { useState } from "react"
import { Input } from "@/components/ui/input"
import { formatCents } from "@/lib/fetch-json"

// <PurchaseDetailsCard>
//
// Right-side summary of what the user is about to buy, rendered inside
// <CheckoutLayout>'s sidebar slot. Shows the product, an editable seat
// count, the live tier breakdown + discount + total, the renewal date, and
// the auto-renewal/guarantee reminder. Seats above `maxSeats` don't commit —
// a "contact us" note appears instead.

export interface QuoteLine {
  label: string
  quantity: number
  unit_amount: number
  amount: number
}

export function PurchaseDetailsCard({
  seats,
  maxSeats,
  onSeatsChange,
  lines,
  discount,
  discountLabel,
  total,
  renewalTotal,
  renewsOn,
}: {
  seats: number
  maxSeats: number
  onSeatsChange: (next: number) => void
  lines: QuoteLine[]
  discount: number
  discountLabel: string | null
  total: number
  renewalTotal: number
  renewsOn: string
}) {
  const [input, setInput] = useState(String(seats))
  const [tooMany, setTooMany] = useState(false)

  function handleChange(raw: string) {
    setInput(raw)
    const parsed = parseInt(raw, 10)
    if (Number.isNaN(parsed) || parsed < 1) return
    if (parsed > maxSeats) {
      setTooMany(true)
      return
    }
    setTooMany(false)
    onSeatsChange(parsed)
  }

  return (
    <div className="space-y-4">
      <dl className="space-y-4">
        <Row label="Product">
          <span className="font-medium text-ink-display">Flywrite</span>
        </Row>
        <Divider />
        <Row label="Number of seats">
          <Input
            type="number"
            min={1}
            max={maxSeats}
            inputMode="numeric"
            className="w-20 text-right"
            value={input}
            onChange={(e) => handleChange(e.target.value)}
            aria-label="Number of seats"
          />
        </Row>
        <p className="text-xs text-ink-muted">
          Each seat is a license key for one person.
        </p>
        {tooMany && (
          <p className="text-xs text-ink-body">
            Need more than {maxSeats} seats?{" "}
            <a href="mailto:support@flywrite.app?subject=Flywrite%20volume%20pricing">
              Contact us
            </a>{" "}
            for volume pricing.
          </p>
        )}
        <Divider />
        <div className="space-y-2">
          {lines.map((line) => (
            <Row
              key={line.label}
              label={`${line.label} · ${line.quantity} × ${formatCents(line.unit_amount)}`}
            >
              {formatCents(line.amount)}
            </Row>
          ))}
          {discount > 0 && (
            <Row label={`Discount${discountLabel ? ` (${discountLabel})` : ""}`}>
              <span className="text-accent">−{formatCents(discount)}</span>
            </Row>
          )}
        </div>
        <Divider />
        <Row label={renewalTotal !== total ? "Total (first year)" : "Total"}>
          <span className="font-semibold text-ink-display">
            {formatCents(total)}
            {/* A first-year-only discount isn't the recurring price, so drop "/year". */}
            {renewalTotal === total ? "/year" : ""}
          </span>
        </Row>
        <Divider />
        {renewalTotal !== total ? (
          <>
            <Row label="Renews">
              <span>{formatCents(renewalTotal)}/year</span>
            </Row>
            <p className="text-xs text-ink-muted">
              Your discount applies to the first year. Renews at{" "}
              {formatCents(renewalTotal)}/year on {renewsOn}.
            </p>
          </>
        ) : (
          <Row label="Renews">{renewsOn}</Row>
        )}
      </dl>
      <div className="space-y-1">
        <small className="block text-ink-muted">Cancel auto-renewal anytime.</small>
        <small className="block text-ink-muted">14-day money-back guarantee.</small>
      </div>
    </div>
  )
}

export function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

export function Divider() {
  return <div aria-hidden className="border-t border-hairline" />
}
